import type { Broker } from '@/mocks/brokers';
import { brokerFilter, brokerRecommendation } from '@/mocks/brokers';
import BrokerButtons from '@/pages/forex-trading/components/BrokerButtons';

const factLabels = brokerRecommendation.cardFactLabels;

function FactRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-0.5 py-2.5 ${
        highlight ? 'border-l-2 border-accent-400 pl-2.5' : ''
      }`}
    >
      <dt
        className={`text-[11px] font-medium uppercase tracking-[0.08em] ${
          highlight ? 'text-accent-700' : 'text-foreground-500'
        }`}
      >
        {label}
      </dt>
      <dd className="text-[13px] leading-snug text-foreground-800">{value}</dd>
    </div>
  );
}

export default function BrokerCard({
  broker,
  highlightCost = false,
}: {
  broker: Broker;
  highlightCost?: boolean;
}) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200 md:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={broker.logo}
            alt={`${broker.name} logo`}
            title={broker.name}
            className="h-11 w-11 shrink-0 rounded-lg border border-background-200 bg-background-100 object-contain"
          />
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-semibold leading-tight text-foreground-950">{broker.name}</h3>
            <p className="mt-0.5 truncate text-[11px] text-foreground-500">{broker.tagline}</p>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
          {brokerRecommendation.pendingLabel}
        </span>
      </div>

      <p className="mt-4 text-[13px] leading-relaxed text-foreground-700">{broker.summary}</p>

      <dl className="mt-4 flex flex-col divide-y divide-background-200 border-t border-background-200">
        <FactRow label={factLabels.platforms} value={broker.card.platforms} />
        <FactRow label={factLabels.accounts} value={broker.card.accounts} highlight={highlightCost} />
        <FactRow label={factLabels.cost} value={broker.card.cost} highlight={highlightCost} />
        <FactRow label={factLabels.funding} value={broker.card.funding} />
      </dl>

      {highlightCost && (
        <p className="mt-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-accent-700">
          <i className="ri-focus-3-line text-xs" />
          {brokerFilter.costBadge}
        </p>
      )}

      <div className="mt-auto pt-5">
        <BrokerButtons broker={broker} />
      </div>
    </div>
  );
}